import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../ui/dialog";
import { Checkbox } from "../ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Textarea } from "../ui/textarea";
import { ScrollArea } from "../ui/scroll-area";
import {
  Users, Plus, Pencil, Trash2, Ban, Check, CalendarIcon, Search,
  Download, Upload, FileDown, RefreshCw, X, AlertTriangle, ArrowUp, ArrowDown, ArrowUpDown
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { COUNTRIES, getFlag, getCountryName } from "./constants";

export const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [tools, setTools] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importMode, setImportMode] = useState("file"); // "file" or "text"
  const [importText, setImportText] = useState("");
  const [sortField, setSortField] = useState("last_access");
  const [sortDir, setSortDir] = useState("desc");
  const { token } = useAuth();
  const fileInputRef = useRef(null);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des utilisateurs");
    }
  }, [token]);

  const fetchTools = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/tools`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTools(response.data);
    } catch (error) {
      console.error("Error fetching tools");
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
    fetchTools();
  }, [fetchUsers, fetchTools]);

  const toggleUserStatus = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/toggle-status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
      toast.success("Statut mis à jour");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
      toast.success("Utilisateur supprimé");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `utilisateurs_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export réussi");
    } catch (error) {
      toast.error("Erreur lors de l'export");
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/import/template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_import_utilisateurs.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Erreur lors du téléchargement du template");
    }
  };

  const handleImportCSV = async () => {
    if (!selectedFile) {
      toast.error("Veuillez sélectionner un fichier");
      return;
    }
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await axios.post(
        `${API}/admin/users/import/csv?skip_duplicates=${skipDuplicates}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setImportResult(response.data);
      
      if (response.data.success_count > 0) {
        toast.success(`${response.data.success_count} utilisateur(s) importé(s)`);
        fetchUsers();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Erreur lors de l'import";
      toast.error(errorMsg);
      setImportResult({ error: errorMsg });
    } finally {
      setImporting(false);
    }
  };

  const handleImportText = async () => {
    if (!importText.trim()) {
      toast.error("Veuillez coller du contenu");
      return;
    }
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const response = await axios.post(
        `${API}/admin/users/import/text`,
        {
          text_content: importText,
          skip_duplicates: skipDuplicates
        },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      setImportResult(response.data);
      if (response.data.success_count > 0) {
        toast.success(`${response.data.success_count} utilisateur(s) importé(s)`);
        fetchUsers();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Erreur lors de l'import";
      toast.error(errorMsg);
      setImportResult({ error: errorMsg });
    } finally {
      setImporting(false);
    }
  };

  const resetImportDialog = () => {
    setSelectedFile(null);
    setImportResult(null);
    setSkipDuplicates(true);
    setImportText("");
    setImportMode("file");
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const filteredUsers = users.filter(user => 
    user.login.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.last_name?.toLowerCase().includes(searchTerm.toLowerCase())
  ).sort((a, b) => {
    let valA, valB;
    switch (sortField) {
      case "login":
        valA = a.login.toLowerCase();
        valB = b.login.toLowerCase();
        return sortDir === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
      case "name":
        valA = `${a.first_name} ${a.last_name}`.toLowerCase();
        valB = `${b.first_name} ${b.last_name}`.toLowerCase();
        return sortDir === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
      case "country":
        valA = a.country_code || "";
        valB = b.country_code || "";
        return sortDir === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
      case "status":
        const statusOrder = (u) => !u.is_active ? 2 : (u.expiration_date && new Date(u.expiration_date) < new Date()) ? 1 : 0;
        valA = statusOrder(a);
        valB = statusOrder(b);
        return sortDir === "asc" ? valA - valB : valB - valA;
      case "daily_limit":
        return sortDir === "asc" ? a.daily_limit - b.daily_limit : b.daily_limit - a.daily_limit;
      case "expiration":
        valA = a.expiration_date ? new Date(a.expiration_date).getTime() : Infinity;
        valB = b.expiration_date ? new Date(b.expiration_date).getTime() : Infinity;
        return sortDir === "asc" ? valA - valB : valB - valA;
      case "last_access":
        valA = a.last_access ? new Date(a.last_access).getTime() : 0;
        valB = b.last_access ? new Date(b.last_access).getTime() : 0;
        return sortDir === "asc" ? valA - valB : valB - valA;
      case "tools":
        valA = a.allowed_tools?.length || 0;
        valB = b.allowed_tools?.length || 0;
        return sortDir === "asc" ? valA - valB : valB - valA;
      default:
        return 0;
    }
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 opacity-30" />;
    return sortDir === "asc" ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" />;
  };

  const getUserStatus = (user) => {
    if (!user.is_active) return { label: "Bloqué", class: "status-blocked" };
    if (user.expiration_date) {
      const expDate = new Date(user.expiration_date);
      if (expDate < new Date()) return { label: "Expiré", class: "status-expired" };
    }
    return { label: "Actif", class: "status-active" };
  };

  return (
    <div className="p-6 animate-fade-in" data-testid="users-management">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-bold tracking-tight">Utilisateurs</h1>
        <div className="flex items-center gap-2">
          {/* Export Button */}
          <Button 
            variant="outline" 
            onClick={handleExportCSV}
            data-testid="export-csv-button"
          >
            <Download className="h-4 w-4 mr-2" />
            Exporter CSV
          </Button>
          
          {/* Import Button */}
          <Dialog open={importDialogOpen} onOpenChange={(open) => {
            setImportDialogOpen(open);
            if (!open) resetImportDialog();
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="import-csv-button">
                <Upload className="h-4 w-4 mr-2" />
                Importer CSV
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Importer des utilisateurs</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Mode Selection Tabs */}
                <Tabs value={importMode} onValueChange={setImportMode} className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="file">
                      <Upload className="h-4 w-4 mr-2" />
                      Fichier CSV
                    </TabsTrigger>
                    <TabsTrigger value="text">
                      <FileDown className="h-4 w-4 mr-2" />
                      Coller le texte
                    </TabsTrigger>
                  </TabsList>
                  
                  {/* File Import Tab */}
                  <TabsContent value="file" className="space-y-4 mt-4">
                    {/* Instructions */}
                    <div className="bg-muted/30 p-4 rounded-sm space-y-2">
                      <p className="text-sm font-medium">Format du fichier CSV :</p>
                      <p className="text-xs text-muted-foreground">
                        Colonnes requises : <code className="text-primary">user</code>, <code className="text-primary">mot_de_passe</code>, <code className="text-primary">date_expiration</code>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Formats de date acceptés : DD/MM/YYYY, YYYY-MM-DD
                      </p>
                      <Button 
                        variant="link" 
                        size="sm" 
                        className="p-0 h-auto text-primary"
                        onClick={handleDownloadTemplate}
                      >
                        <FileDown className="h-3 w-3 mr-1" />
                        Télécharger le template
                      </Button>
                    </div>
                    
                    {/* File Input */}
                    <div className="space-y-2">
                      <Label>Fichier CSV</Label>
                      <Input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.txt"
                        onChange={(e) => {
                          setSelectedFile(e.target.files[0]);
                          setImportResult(null);
                        }}
                        data-testid="import-file-input"
                      />
                    </div>
                  </TabsContent>
                  
                  {/* Text Paste Tab */}
                  <TabsContent value="text" className="space-y-4 mt-4">
                    {/* Instructions */}
                    <div className="bg-muted/30 p-4 rounded-sm space-y-2">
                      <p className="text-sm font-medium">Collez vos utilisateurs :</p>
                      <p className="text-xs text-muted-foreground">
                        Format : <code className="text-primary">email;mot_de_passe;date_expiration</code> (un par ligne)
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Exemple : <code className="text-primary">user@email.com;password123;15/01/2027</code>
                      </p>
                    </div>
                    
                    {/* Text Area */}
                    <div className="space-y-2">
                      <Label>Contenu à importer</Label>
                      <textarea
                        className="w-full h-[200px] p-3 text-sm font-mono bg-background border border-input rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder="Collez vos utilisateurs ici...&#10;&#10;email@example.com;password123;15/01/2027&#10;autre@email.com;motdepasse;20/06/2026"
                        value={importText}
                        onChange={(e) => {
                          setImportText(e.target.value);
                          setImportResult(null);
                        }}
                        data-testid="import-text-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        {importText.split('\n').filter(l => l.trim()).length} ligne(s)
                      </p>
                    </div>
                  </TabsContent>
                </Tabs>
                
                {/* Options */}
                <div className="flex items-center gap-2">
                  <Switch
                    id="skip-duplicates"
                    checked={skipDuplicates}
                    onCheckedChange={setSkipDuplicates}
                    data-testid="skip-duplicates-switch"
                  />
                  <Label htmlFor="skip-duplicates" className="text-sm">
                    Ignorer les doublons (utilisateurs existants)
                  </Label>
                </div>
                
                {/* Import Button */}
                <Button 
                  onClick={importMode === "file" ? handleImportCSV : handleImportText}
                  disabled={(importMode === "file" ? !selectedFile : !importText.trim()) || importing}
                  className="w-full"
                  data-testid="start-import-button"
                >
                  {importing ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Import en cours...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Importer
                    </>
                  )}
                </Button>
                
                {/* Import Results */}
                {importResult && !importResult.error && (
                  <div className="space-y-3 border-t border-border pt-4">
                    <h4 className="font-medium">Rapport d'import</h4>
                    
                    {/* Summary */}
                    <div className="grid grid-cols-4 gap-2">
                      <div className="bg-muted/30 p-3 rounded-sm text-center">
                        <p className="text-2xl font-bold">{importResult.total_lines}</p>
                        <p className="text-xs text-muted-foreground">Total</p>
                      </div>
                      <div className="bg-green-500/10 p-3 rounded-sm text-center">
                        <p className="text-2xl font-bold text-green-500">{importResult.success_count}</p>
                        <p className="text-xs text-muted-foreground">Succès</p>
                      </div>
                      <div className="bg-yellow-500/10 p-3 rounded-sm text-center">
                        <p className="text-2xl font-bold text-yellow-500">{importResult.skipped_count}</p>
                        <p className="text-xs text-muted-foreground">Ignorés</p>
                      </div>
                      <div className="bg-red-500/10 p-3 rounded-sm text-center">
                        <p className="text-2xl font-bold text-red-500">{importResult.error_count}</p>
                        <p className="text-xs text-muted-foreground">Erreurs</p>
                      </div>
                    </div>
                    
                    {/* Details */}
                    {importResult.results && importResult.results.length > 0 && (
                      <ScrollArea className="h-[200px] border border-border rounded-sm">
                        <div className="p-2 space-y-1">
                          {importResult.results.map((result, idx) => (
                            <div 
                              key={idx}
                              className={`flex items-center gap-2 text-xs p-2 rounded ${
                                result.status === 'success' ? 'bg-green-500/10' :
                                result.status === 'skipped' ? 'bg-yellow-500/10' :
                                'bg-red-500/10'
                              }`}
                            >
                              {result.status === 'success' ? (
                                <Check className="h-3 w-3 text-green-500 flex-shrink-0" />
                              ) : result.status === 'skipped' ? (
                                <AlertTriangle className="h-3 w-3 text-yellow-500 flex-shrink-0" />
                              ) : (
                                <X className="h-3 w-3 text-red-500 flex-shrink-0" />
                              )}
                              <span className="font-mono">L{result.line_number}</span>
                              <span className="font-medium">{result.user}</span>
                              <span className="text-muted-foreground truncate">{result.message}</span>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    )}
                  </div>
                )}
                
                {/* Error Message */}
                {importResult?.error && (
                  <div className="bg-red-500/10 p-4 rounded-sm">
                    <p className="text-sm text-red-500">{importResult.error}</p>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Add User Button */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-primary text-primary-foreground"
                onClick={() => setEditingUser(null)}
                data-testid="add-user-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nouvel Utilisateur
              </Button>
            </DialogTrigger>
            <UserFormDialog 
              user={editingUser}
              tools={tools}
              token={token}
              onSuccess={() => {
                setDialogOpen(false);
                fetchUsers();
              }}
            />
          </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-input border-transparent focus:border-primary"
            data-testid="search-users"
          />
        </div>
      </div>

      {/* Users Table */}
      <Card className="bg-card border-border overflow-hidden">
        <ScrollArea className="h-[calc(100vh-280px)]">
          <div className="overflow-x-auto">
          <table className="w-full data-table text-xs md:text-sm" data-testid="users-table">
            <thead className="sticky top-0 z-10 bg-muted">
              <tr>
                <th className="text-left py-2 px-1.5 md:px-4 cursor-pointer select-none hover:text-primary transition-colors whitespace-nowrap" onClick={() => handleSort("country")}>
                  <div className="flex items-center gap-1"><SortIcon field="country" /></div>
                </th>
                <th className="text-left py-2 px-1.5 md:px-4 cursor-pointer select-none hover:text-primary transition-colors" onClick={() => handleSort("login")}>
                  <div className="flex items-center gap-1">Login <SortIcon field="login" /></div>
                </th>
                <th className="hidden md:table-cell text-left py-2 px-4 cursor-pointer select-none hover:text-primary transition-colors" onClick={() => handleSort("name")}>
                  <div className="flex items-center gap-1">Nom <SortIcon field="name" /></div>
                </th>
                <th className="text-left py-2 px-1.5 md:px-4 cursor-pointer select-none hover:text-primary transition-colors whitespace-nowrap" onClick={() => handleSort("status")}>
                  <div className="flex items-center gap-1">Statut <SortIcon field="status" /></div>
                </th>
                <th className="hidden sm:table-cell text-left py-2 px-1.5 md:px-4 cursor-pointer select-none hover:text-primary transition-colors whitespace-nowrap" onClick={() => handleSort("daily_limit")}>
                  <div className="flex items-center gap-1">Limite <SortIcon field="daily_limit" /></div>
                </th>
                <th className="hidden lg:table-cell text-left py-2 px-4 cursor-pointer select-none hover:text-primary transition-colors" onClick={() => handleSort("expiration")}>
                  <div className="flex items-center gap-1">Expiration <SortIcon field="expiration" /></div>
                </th>
                <th className="hidden lg:table-cell text-left py-2 px-4 cursor-pointer select-none hover:text-primary transition-colors" onClick={() => handleSort("last_access")}>
                  <div className="flex items-center gap-1">Dernier Acces <SortIcon field="last_access" /></div>
                </th>
                <th className="hidden lg:table-cell text-left py-2 px-4 cursor-pointer select-none hover:text-primary transition-colors" onClick={() => handleSort("tools")}>
                  <div className="flex items-center gap-1">Outils <SortIcon field="tools" /></div>
                </th>
                <th className="text-right py-2 px-1.5 md:px-4 whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => {
                const status = getUserStatus(user);
                return (
                  <tr key={user.id} className="border-t border-border" data-testid={`user-row-${user.id}`}>
                    <td className="py-1.5 px-1.5 md:px-4">
                      <span className="flag-icon text-base" title={getCountryName(user.country_code)}>
                        {getFlag(user.country_code)}
                      </span>
                    </td>
                    <td className="py-1.5 px-1.5 md:px-4 font-mono">
                      <span className="truncate max-w-[80px] md:max-w-none inline-block align-middle">{user.login}</span>
                      {user.is_admin && (
                        <Badge variant="outline" className="ml-1 text-primary border-primary/30 text-[9px] px-1">
                          A
                        </Badge>
                      )}
                    </td>
                    <td className="hidden md:table-cell py-1.5 px-4">
                      {user.first_name} {user.last_name}
                    </td>
                    <td className="py-1.5 px-1.5 md:px-4">
                      <Badge className={status.class + " text-[9px] md:text-xs px-1 md:px-2"}>{status.label}</Badge>
                    </td>
                    <td className="hidden sm:table-cell py-1.5 px-1.5 md:px-4 font-mono text-[10px] md:text-xs">
                      <div><span className="text-accent">{user.host_reveals_today ?? 0}</span><span className="text-muted-foreground">/{user.host_global_limit ?? 50}</span></div>
                      <div className="text-muted-foreground text-[9px]">{user.host_provider_limit ?? 5}/f/j</div>
                    </td>
                    <td className="hidden lg:table-cell py-1.5 px-4 font-mono text-sm">
                      {user.expiration_date ? (
                        <span className={new Date(user.expiration_date) < new Date() ? "text-destructive" : ""}>
                          {format(new Date(user.expiration_date), "dd/MM/yyyy")}
                        </span>
                      ) : (
                        <Badge variant="outline" className="text-primary border-primary/30">
                          Illimite
                        </Badge>
                      )}
                    </td>
                    <td className="hidden lg:table-cell py-1.5 px-4 font-mono text-xs text-muted-foreground">
                      {user.last_access 
                        ? format(new Date(user.last_access), "dd/MM/yy HH:mm")
                        : "-"
                      }
                    </td>
                    <td className="hidden lg:table-cell py-1.5 px-4">
                      <Badge variant="secondary" className="font-mono">
                        {user.allowed_tools?.length || 0}
                      </Badge>
                    </td>
                    <td className="py-1.5 px-1.5 md:px-4">
                      <div className="flex items-center justify-end gap-0.5">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => setEditingUser(user)}
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          </DialogTrigger>
                          <UserFormDialog 
                            user={user}
                            tools={tools}
                            token={token}
                            onSuccess={fetchUsers}
                          />
                        </Dialog>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => toggleUserStatus(user.id)}
                          className={`h-7 w-7 ${user.is_active ? "text-destructive hover:bg-destructive/10" : "text-primary hover:bg-primary/10"}`}
                          data-testid={`toggle-user-${user.id}`}
                        >
                          {user.is_active ? <Ban className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
                        </Button>
                        {!user.is_admin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:bg-destructive/10"
                            onClick={() => deleteUser(user.id)}
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </ScrollArea>
      </Card>
    </div>
  );
};

// User Form Dialog Component
const UserFormDialog = ({ user, tools, token, onSuccess }) => {
  const [formData, setFormData] = useState({
    login: user?.login || "",
    password: "",
    email: user?.email || "",
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    country_code: user?.country_code || "FR",
    is_active: user?.is_active ?? true,
    is_admin: user?.is_admin ?? false,
    daily_limit: user?.daily_limit || 50,
    host_global_limit: user?.host_global_limit || null,
    host_provider_limit: user?.host_provider_limit || null,
    expiration_date: user?.expiration_date ? new Date(user.expiration_date) : null,
    allowed_tools: user?.allowed_tools || []
  });
  const [loading, setLoading] = useState(false);
  const [adminDefaults, setAdminDefaults] = useState({ global: 50, provider: 5 });

  useEffect(() => {
    const fetchDefaults = async () => {
      try {
        const res = await axios.get(`${API}/admin/settings/reveal-quotas`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAdminDefaults({
          global: res.data.global_daily_limit,
          provider: res.data.per_provider_daily_limit
        });
      } catch (e) { /* ignore */ }
    };
    fetchDefaults();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        expiration_date: formData.expiration_date 
          ? formData.expiration_date.toISOString() 
          : null
      };

      if (!payload.password) {
        delete payload.password;
      }

      if (user) {
        await axios.put(`${API}/admin/users/${user.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Utilisateur mis à jour");
      } else {
        await axios.post(`${API}/admin/users`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Utilisateur créé");
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setLoading(false);
    }
  };

  const toggleTool = (toolId) => {
    setFormData(prev => ({
      ...prev,
      allowed_tools: prev.allowed_tools.includes(toolId)
        ? prev.allowed_tools.filter(id => id !== toolId)
        : [...prev.allowed_tools, toolId]
    }));
  };

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="user-form-dialog">
      <DialogHeader>
        <DialogTitle>{user ? "Modifier l'utilisateur" : "Nouvel utilisateur"}</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Login *</Label>
            <Input
              value={formData.login}
              onChange={(e) => setFormData({ ...formData, login: e.target.value })}
              required
              data-testid="form-login"
            />
          </div>
          <div className="space-y-2">
            <Label>{user ? "Nouveau mot de passe" : "Mot de passe *"}</Label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required={!user}
              placeholder={user ? "Laisser vide pour ne pas changer" : ""}
              data-testid="form-password"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Prénom</Label>
            <Input
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              data-testid="form-firstname"
            />
          </div>
          <div className="space-y-2">
            <Label>Nom</Label>
            <Input
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              data-testid="form-lastname"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Email</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              data-testid="form-email"
            />
          </div>
          <div className="space-y-2">
            <Label>Pays</Label>
            <Select
              value={formData.country_code}
              onValueChange={(value) => setFormData({ ...formData, country_code: value })}
            >
              <SelectTrigger data-testid="form-country">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((country) => (
                  <SelectItem key={country.code} value={country.code}>
                    <span className="mr-2">{country.flag}</span>
                    {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Limite quotidienne</Label>
            <Input
              type="number"
              value={formData.daily_limit}
              onChange={(e) => setFormData({ ...formData, daily_limit: parseInt(e.target.value) || 0 })}
              data-testid="form-daily-limit"
            />
          </div>
          <div className="space-y-2">
            <Label>Date d'expiration</Label>
            <div className="flex items-center gap-2 mb-2">
              <Switch
                id="unlimited-switch"
                checked={!formData.expiration_date}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setFormData({ ...formData, expiration_date: null });
                  } else {
                    // Set default expiration to 1 year from now
                    const oneYearFromNow = new Date();
                    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
                    setFormData({ ...formData, expiration_date: oneYearFromNow });
                  }
                }}
                data-testid="unlimited-switch"
              />
              <Label htmlFor="unlimited-switch" className="text-sm cursor-pointer">
                Accès illimité (pas d'expiration)
              </Label>
            </div>
            {!formData.expiration_date ? (
              <div className="p-3 bg-primary/10 border border-primary/30 rounded-sm">
                <p className="text-sm text-primary flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  Cet utilisateur a un accès illimité
                </p>
              </div>
            ) : (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start" data-testid="form-expiration">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(formData.expiration_date, "dd/MM/yyyy", { locale: fr })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.expiration_date}
                    onSelect={(date) => setFormData({ ...formData, expiration_date: date })}
                    locale={fr}
                  />
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>

        {/* Host reveal limits */}
        <div className="space-y-2">
          <Label className="text-primary text-sm">Limites "Trouver votre host"</Label>
          <div className="grid grid-cols-2 gap-4 p-3 bg-muted/30 rounded-sm border border-border">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Limite globale/jour</Label>
              <Input
                type="number"
                min={0}
                value={formData.host_global_limit ?? ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData({ ...formData, host_global_limit: val === "" ? null : parseInt(val) || 0 });
                }}
                placeholder={`Par défaut: ${adminDefaults.global}`}
                data-testid="form-host-global-limit"
              />
              <p className="text-xs text-muted-foreground">Vide = défaut admin ({adminDefaults.global})</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Limite par fournisseur/jour</Label>
              <Input
                type="number"
                min={0}
                value={formData.host_provider_limit ?? ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData({ ...formData, host_provider_limit: val === "" ? null : parseInt(val) || 0 });
                }}
                placeholder={`Par défaut: ${adminDefaults.provider}`}
                data-testid="form-host-provider-limit"
              />
              <p className="text-xs text-muted-foreground">Vide = défaut admin ({adminDefaults.provider})</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <Switch
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              data-testid="form-is-active"
            />
            <Label>Compte actif</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={formData.is_admin}
              onCheckedChange={(checked) => setFormData({ ...formData, is_admin: checked })}
              data-testid="form-is-admin"
            />
            <Label>Administrateur</Label>
          </div>
        </div>

        {/* Tools selection */}
        <div className="space-y-2">
          <Label>Outils autorisés</Label>
          <div className="grid grid-cols-2 gap-2 p-3 bg-muted/30 rounded-sm border border-border">
            {tools.map((tool) => (
              <div key={tool.id} className="flex items-center gap-2">
                <Checkbox
                  checked={formData.allowed_tools.includes(tool.id)}
                  onCheckedChange={() => toggleTool(tool.id)}
                  data-testid={`form-tool-${tool.id}`}
                />
                <Label className="cursor-pointer text-sm">{tool.name}</Label>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button type="submit" disabled={loading} data-testid="form-submit">
            {loading ? "Sauvegarde..." : (user ? "Mettre à jour" : "Créer")}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
};

